/**
 * Все строки интерфейса — русский, язык по умолчанию.
 *
 * Текст в JSX напрямую не пишется — только отсюда. Ключи группируются по
 * экранам и по общим понятиям. Английский словарь (`en.ts`) обязан повторять
 * эту структуру целиком: пропущенный ключ — ошибка типов, а не пустое место
 * на экране.
 *
 * Строки экранов переезжают сюда по мере их переписывания; пока здесь
 * оболочка и общие компоненты.
 */
export const ru = {
  app: {
    name: 'Faceta',
  },
  nav: {
    path: 'Путь',
    sections: 'Разделы',
    collapse: 'Свернуть колонку',
    expand: 'Развернуть колонку',
    openMenu: 'Открыть меню',
    closeMenu: 'Закрыть меню',
    sites: 'Сайты',
    tenants: 'Тенанты',
    home: 'Главная',
    content: 'Содержимое',
    media: 'Медиа',
    constants: 'Константы',
    pages: 'Страницы',
    models: 'Модели',
    forms: 'Формы',
    users: 'Пользователи',
    automations: 'Автоматизации',
    shop: 'Магазин',
    components: 'Компоненты',
    favorites: 'Избранное',
    navigation: 'Навигация',
    otherSites: 'Другие сайты',
    model: 'Модели',
    transfer: 'Перенос',
    showMore: (n: number) => `Ещё ${String(n)}`,
    showLess: 'Свернуть список',
    settings: 'Настройки',
    team: 'Команда',
    activity: 'Журнал',
    search: 'Поиск',
    searchHint: 'Поиск и переходы',
    more: 'Ещё',
    allSites: 'Все сайты',
    record: 'Запись',
  },
  env: {
    devOnlyNote:
      'У вас ограничение «только Development». Это общее для обоих окружений — правка задела бы Production, поэтому здесь только просмотр.',
    switcher: 'Окружение',
    production: 'Production',
    development: 'Development',
    stripe:
      'Development — тестовое окружение: правки видны на тестовых стендах сайта, но не на боевом.',
    missingDevelopment: 'У этого сайта ещё нет окружения Development',
    tabPrefix: '[DEV]',
  },
  userMenu: {
    theme: 'Тема',
    themeSystem: 'Как в системе',
    themeDark: 'Тёмная',
    themeLight: 'Светлая',
    language: 'Язык',
    signOut: 'Выйти',
    signOutFailed: 'Не удалось выйти',
  },
  impersonation: {
    text: 'Вы работаете от имени',
    back: 'Вернуться к себе',
    backFailed: 'Не удалось вернуться в свою учётную запись',
  },
  siteHome: {
    collection: 'Коллекция',
    single: 'Одиночка',
    mediaHint: 'Картинки и файлы сайта',
    emptyTitle: 'Коллекций пока нет',
    emptyShaper:
      'Опишите первую коллекцию в «Моделях»: какие у записей поля. После этого она появится в меню слева.',
    emptyEditor:
      'Коллекции заводит администратор или разработчик сайта — как только появятся, они будут здесь и в меню слева.',
    openModel: 'Открыть «Модели»',
  },
  sites: {
    title: 'Сайты',
    subtitle: 'У каждого сайта два окружения — Production и Development — со своими данными.',
    create: 'Создать сайт',
    createFirst: 'Создать первый сайт',
    search: 'Поиск по названию и короткому имени',
    allTenants: 'Все тенанты',
    tenant: 'Тенант',
    trash: 'Корзина',
    noAddress: 'Адрес не указан',
    noAddressHint: 'Сайт ещё никуда не подключён — адрес задаётся в настройках сайта',
    settings: 'Настройки',
    actions: (name: string) => `Действия с сайтом «${name}»`,
    open: 'Открыть',
    remove: 'Удалить',
    restore: 'Восстановить',
    restored: (name: string) => `Сайт «${name}» восстановлен`,
    restoreFailed: 'Не удалось восстановить',
    removed: (name: string) => `Сайт «${name}» в корзине`,
    undo: 'Вернуть',
    undoHint: 'Восстановить сайт можно в корзине',
    removeTitle: (name: string) => `Удалить «${name}»?`,
    removePlain: 'Сайт уедет в корзину вместе с окружениями. Восстановить можно оттуда же.',
    loadFailed: 'Не удалось загрузить сайты',
    loadFailedHint: 'Сервер не ответил. Данные не потеряны — попробуйте ещё раз.',
    retry: 'Повторить',
    emptyTitle: 'Пока нет ни одного сайта',
    emptyText:
      'Сайт — это отдельное содержимое со своим адресом. Вместе с ним заводятся окружения Production и Development.',
    trashEmpty: 'Корзина пуста',
    notFoundTitle: 'Ничего не найдено',
    notFoundText: 'Под поиск и выбранного тенанта не подходит ни один сайт.',
    reset: 'Сбросить',
    more: 'Показать ещё',
    deletedAt: 'В корзине',
    unknownError: 'Попробуйте ещё раз.',
  },
  settings: {
    title: 'Настройки сайта',
    general: 'Сайт',
    name: 'Название',
    slug: 'Короткое имя',
    slugHint:
      'Латиница, цифры и дефис. Им сайт называется в адресах API — после смены старые запросы перестанут работать.',
    save: 'Сохранить',
    saved: 'Сохранено',
    failed: 'Не удалось сохранить',
    addresses: 'Адреса',
    addressesText:
      'Где живёт сайт в каждом окружении. По этому адресу Faceta открывает предпросмотр, собирает ссылки в письмах и разрешает браузеру обращаться к API.',
    url: 'Адрес сайта',
    urlHint: 'С http:// или https://. Пусто — сайт ещё не подключён.',
    extra: 'Ещё разрешённые адреса',
    extraHint:
      'По одному в строке, без пути: https://www.shop.ru. Нужны редко — например, если сайт открывается и с www, и без.',
    more: 'Дополнительно',
    saveAddress: 'Сохранить адрес',
    noAccess: 'Настройки сайта меняют администратор и разработчик сайта.',
    languages: 'Языки',
    languagesText:
      'На каких языках содержимое сайта. Первый — основной: его значения лежат в полях записи, остальные — переводы полей, отмеченных «Переводимое». Пропущенный перевод сайт заменит основным языком.',
    main: 'основной',
    makeMain: 'Сделать основным',
    removeLanguage: (code: string) => `Убрать ${code}`,
    addLanguage: 'Добавить язык',
    languageCode: 'Код: ru, en, kk, pt-BR',
    mainChanged:
      'Смена основного языка не переносит значения: в полях останется прежний текст. Делайте это до наполнения сайта.',
  },
  constants: {
    title: 'Константы',
    subtitle:
      'Телефон, почта, ссылки на соцсети — значения, которые сайт берёт по ключу. Черновиков нет: сохранённое сразу на сайте.',
    add: 'Добавить константу',
    empty: 'Констант пока нет',
    emptyHint:
      'Заведите телефон, почту или ссылку на соцсеть — сайт получит их одним запросом /v1/constants.',
    emptyReader: 'Констант пока нет. Завести их может разработчик или администратор сайта.',
    general: 'Общие',
    edit: 'Изменить',
    delete: 'Удалить',
    actions: 'Действия с константой',
    copyKey: 'Скопировать ключ',
    saving: 'Сохраняем…',
    saved: 'Сохранено',
    failed: 'Не сохранилось',
    stale: 'Кто-то изменил константу — показано новое значение',
    createTitle: 'Новая константа',
    editTitle: 'Константа',
    name: 'Название',
    namePlaceholder: 'Телефон',
    key: 'Ключ',
    keyHint: 'По нему сайт находит значение: constants.phone. Латиница, цифры и подчёркивания.',
    keyChanged:
      'Сайт перестанет находить значение по старому ключу — поправьте его и в коде сайта.',
    type: 'Вид',
    widget: 'Редактор',
    section: 'Раздел',
    sectionHint: 'Группа на этом экране: «Контакты», «Соцсети». Пусто — «Общие».',
    hint: 'Подсказка',
    hintHint: 'Что сюда писать — увидит тот, кто заполняет.',
    save: 'Сохранить',
    create: 'Завести',
    cancel: 'Отмена',
    created: 'Константа заведена',
    deleteTitle: (name: string) => `Удалить «${name}»?`,
    deleteText: (key: string) =>
      `Сайт перестанет получать значение по ключу ${key}. Вернуть константу можно только заново.`,
    deleted: 'Константа удалена',
    noAccess: 'Нет доступа к константам этого сайта',
    moveUp: 'Выше',
    moveDown: 'Ниже',
  },
  team: {
    title: 'Команда',
    subtitle:
      'Кто работает с сайтом. Роль — что человек может; охват — весь тенант или только этот сайт. Приглашение — ссылка, которую нужно передать человеку самому.',
    roles: {
      admin: 'Администратор',
      developer: 'Разработчик',
      editor: 'Редактор',
      author: 'Автор',
      viewer: 'Наблюдатель',
      support: 'Поддержка',
    } as Record<string, string>,
    roleHints: {
      admin: 'Всё, включая людей и настройки',
      developer: 'Модели, настройки и содержимое, без людей',
      editor: 'Любые записи и публикация',
      author: 'Черновики, без публикации',
      viewer: 'Только смотреть',
      support: 'Диалоги с посетителями (появятся позже)',
    } as Record<string, string>,
    email: 'Адрес почты',
    role: 'Роль',
    scope: 'Охват',
    wholeTenant: 'Весь тенант',
    thisSite: 'Только этот сайт',
    site: 'Сайт',
    chooseSite: 'Выберите сайт',
    devOnly: 'Только Development',
    devOnlyHint: 'Production человеку не виден вовсе',
    devOnlyBadge: 'только dev',
    invite: 'Пригласить',
    invitations: 'Приглашения',
    people: 'Люди',
    nobody: 'Пока никого нет — пригласите первого.',
    loading: 'Загружаем…',
    actions: 'Действия',
    changeRole: 'Сменить роль',
    setDevOnly: 'Ограничить Development',
    unsetDevOnly: 'Снять ограничение',
    remove: 'Снять роль',
    revoke: (email: string) => `Отозвать приглашение для ${email}`,
    enterAs: 'Войти от его имени',
    roleChanged: 'Роль изменена',
    removed: 'Роль снята',
    revoked: 'Приглашение отозвано',
    failed: 'Не удалось выполнить действие',
    chooseSiteError: 'Для роли на сайте выберите сайт',
    adminNoDevOnly: 'У администратора не бывает ограничения «только Development»',
    linkNote:
      'Передайте эту ссылку человеку. Второй раз она не покажется — в базе хранится только её отпечаток.',
    copy: 'Копировать',
    copied: 'Скопировано',
    copyFailed: 'Не удалось скопировать',
    copyFailedHint: 'Выделите ссылку и скопируйте вручную.',
    statuses: { active: 'работает', invited: 'приглашён', blocked: 'заблокирован' } as Record<
      string,
      string
    >,
    impersonated: (email: string) => `Вы работаете от имени ${email}`,
    impersonatedHint: 'Вернуться в свою учётную запись можно полосой сверху.',
    impersonateFailed: 'Не удалось войти от имени',
    tenantTitle: (name: string) => `Люди тенанта «${name}»`,
    noAccess: 'Командой сайта управляет его администратор.',
  },
  live: {
    offline: 'Нет связи с сервером — изменения других людей пока не видны. Переподключаемся…',
    viewing: (name: string) => `${name} смотрит`,
    editingField: (name: string, field: string) => `${name} правит «${field}»`,
    support: 'поддержка',
    savedByOther: 'Запись сохранили в другом месте',
    savedByOtherHint: 'У вас есть несохранённые правки, поэтому форма не обновилась сама.',
    takeTheirs: 'Взять их версию',
  },
  activity: {
    title: 'Журнал',
    siteSubtitle: 'Кто, что и когда менял на сайте. Записи, схема, медиа, адреса и люди.',
    platformSubtitle: 'Все события всех тенантов, включая вход от чужого имени.',
    empty: 'Пока ничего не происходило.',
    emptyHint: 'Как только кто-то что-то поменяет, это появится здесь.',
    more: 'Показать ещё',
    failed: 'Не удалось загрузить журнал',
    today: 'Сегодня',
    yesterday: 'Вчера',
    deletedUser: 'Удалённая учётная запись',
    asSupport: (email: string) => `от имени — на самом деле ${email}`,
    actions: {
      'record.create': 'создана запись',
      'record.save': 'сохранён черновик',
      'record.publish': 'опубликована запись',
      'record.unpublish': 'снята с сайта запись',
      'record.delete': 'удалена запись',
      'record.restore': 'восстановлена запись',
      'record.revert': 'возвращена прежняя версия записи',
      'record.discard': 'отменены неопубликованные правки записи',
      'record.transfer': 'перенесена запись с Development',
      'type.create': 'создан тип',
      'type.update': 'изменён тип',
      'type.reorder': 'изменён порядок типов в меню',
      'type.delete': 'удалён тип',
      'type.restore': 'восстановлен тип',
      'field.create': 'добавлено поле',
      'field.update': 'изменено поле',
      'field.hide': 'скрыто поле',
      'field.restore': 'возвращено поле',
      'field.reorder': 'изменён порядок полей типа',
      'field.change_type': 'изменён вид поля',
      'schema.transfer': 'перенесена схема',
      'key.rotate': 'заменён ключ API',
      'schema.rollback': 'откачен перенос схемы',
      'environment.refresh': 'Development обновлён данными Production',
      'media.upload': 'загружен файл',
      'media.move': 'файлы переложены в папку',
      'media.purge': 'удалено из корзины навсегда',
      'media.replace': 'файл заменён',
      'folder.create': 'заведена папка',
      'folder.update': 'изменена папка',
      'folder.delete': 'папка убрана в корзину',
      'folder.restore': 'папка возвращена из корзины',
      'constant.create': 'заведена константа',
      'constant.update': 'изменена константа',
      'constant.delete': 'удалена константа',
      'constant.reorder': 'изменён порядок констант',
      'menu.create': 'заведено меню',
      'menu.update': 'изменено меню',
      'menu.delete': 'удалено меню',
      'record.reorder': 'изменён порядок записей',
      'record.stage': 'сменён статус записи',
      'media.update': 'изменён файл',
      'media.delete': 'удалён файл',
      'media.restore': 'восстановлен файл',
      'site.create': 'создан сайт',
      'site.update': 'изменены название или короткое имя сайта',
      'site.delete': 'удалён сайт',
      'site.restore': 'восстановлен сайт',
      'site.address': 'изменён адрес окружения',
      'tenant.create': 'создан тенант',
      'tenant.update': 'изменён тенант',
      'tenant.delete': 'удалён тенант',
      'tenant.restore': 'восстановлен тенант',
      'member.invite': 'приглашение для',
      'member.revoke': 'отозвано приглашение для',
      'member.role': 'смена роли у',
      'member.remove': 'снята роль у',
      'member.join': 'принято приглашение',
      'impersonation.start': 'вход от имени',
      'impersonation.stop': 'возврат из входа от имени',
    } as Record<string, string>,
  },
  login: {
    title: 'Вход в Faceta',
    subtitle: 'Адрес почты и пароль администратора.',
    email: 'Адрес почты',
    password: 'Пароль',
    submit: 'Войти',
    already: 'Вы уже вошли как',
    toSites: 'К сайтам',
    networkError: 'Не удалось связаться с сервером. Попробуйте ещё раз.',
    failed: 'Не удалось войти. Попробуйте ещё раз.',
    switchTheme: 'Сменить тему',
    switchLanguage: 'Switch to English',
  },
  invite: {
    checking: 'Проверяем приглашение…',
    brokenTitle: 'Ссылка не работает',
    brokenText:
      'Приглашение просрочено, уже использовано или отозвано. Попросите того, кто вас приглашал, выписать новое.',
    title: 'Приглашение в Faceta',
    into: 'Вас приглашают в',
    asRole: {
      tenant_admin: 'администратором тенанта',
      site_admin: 'администратором сайта',
      site_editor: 'редактором сайта',
    } as Record<string, string>,
    onSite: 'на сайт',
    address: 'Адрес',
    name: 'Как вас зовут',
    nameHint: 'Это имя увидят коллеги в списке людей.',
    namePlaceholder: 'Марина Ковалёва',
    newPassword: 'Придумайте пароль',
    password: 'Пароль',
    existingHint: 'У вас уже есть учётная запись — войдёте прежним паролем.',
    optional: 'Можно оставить пустым',
    accept: 'Принять приглашение',
    accepted: 'Приглашение принято',
    acceptedHint: 'Теперь можно войти.',
    failed: 'Не удалось принять приглашение.',
  },
  notFound: {
    title: 'Такой страницы нет',
    text: 'Возможно, адрес набран с опечаткой, или того, на что он ведёт, больше нет.',
    back: 'К сайтам',
  },
  date: {
    today: 'Сегодня',
    tomorrow: 'Завтра',
    nextWeek: 'Через неделю',
    clear: 'Убрать дату',
    prevMonth: 'Предыдущий месяц',
    nextMonth: 'Следующий месяц',
  },
  time: {
    justNow: 'только что',
    minutesAgo: (n: number) => `${String(n)} мин назад`,
    hoursAgo: (n: number) => `${String(n)} ч назад`,
    yesterday: 'вчера',
  },
};

export type Dictionary = typeof ru;
